import { useState, useEffect, useRef, useCallback } from 'react';

import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchGuestsWorkspaces, fetchWorkspaces, reorderWorkspaces, updateWorkspaceOrder } from '../../redux/Slices/workspaceSlice';
import { createPage } from '../../redux/Slices/pageSlice';

import './scss/workspace.scss';
import ZoobbePremiumCard from '../Global/ZoobbePremiumCard';
import ZoobbeBlackFridayCard from '../Global/ZoobbeBlackFridayCard';
import BouncingDots from '../Global/BouncingDots';
import { isWorkspaceAdmin } from '../../utils/helpers';


// Pragmatic DND imports
import {
    draggable,
    dropTargetForElements,
    monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
    attachClosestEdge,
    extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { Add, ArrowRightAlt } from '../icons';
import KeyboardArrowRight from '../icons/KeyboardArrowRight';

// Draggable Workspace Item Component
const DraggableWorkspaceItem = ({ workspace, index, onReorder, openWorkspaces, toggleOptions, setOpenWorkspaces }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [isDropTarget, setIsDropTarget] = useState(false);
    const [dropEdge, setDropEdge] = useState(null);
    const elementRef = useRef(null);
    const isDraggingRef = useRef(false);

    // Get current user to check workspace admin status
    const user = useSelector((state) => state.user.user);
    const userId = user?.user?._id;
    const isAdmin = isWorkspaceAdmin(workspace, userId);
    const subscription = workspace?.subscription;

    // A workspace is on a paid plan when it has a paid tier AND an entitled
    // status (active / trialing). Lifetime plans (DealMirror/AppSumo) are stored
    // active, so they count too. An 'incomplete' record (plan written but payment
    // not confirmed) does NOT count — the upsell should still show.
    const PAID_TIERS = ['Standard', 'Premium', 'Enterprise', 'AppSumo', 'DealMirror'];
    const hasPaidPlan = subscription &&
        PAID_TIERS.includes(subscription.planType) &&
        ['active', 'trialing'].includes(subscription.status);
    const showUpgradeCard = isAdmin && !hasPaidPlan;

    // Determine CTA mode for the upgrade card:
    // - If trial expired (was trialing but days remaining <= 0): show 'upgrade'
    // - Otherwise (Free plan, never started trial): show 'start_trial'
    const isActiveTrialing = subscription?.status === 'trialing' &&
        subscription?.trialEndDate &&
        new Date(subscription.trialEndDate) > new Date();
    const hasExpiredTrial = subscription?.trialUsed && !isActiveTrialing && subscription?.planType === 'Free';
    const ctaMode = hasExpiredTrial ? 'upgrade' : 'start_trial';


    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const isActive = (path) => {
        const pathname = location.pathname;
        const segments = pathname.split('/').filter(Boolean);

        // Extract workspace ID from URL: /w/:workspaceId/...
        // or for pages: /w/:workspaceId/pages/...
        let urlWorkspaceId = null;
        if (segments[0] === 'w' && segments[1]) {
            urlWorkspaceId = segments[1];
        }

        // Only consider active if we are in the context of THIS specific workspace
        if (workspace.shortId !== urlWorkspaceId) return false;

        if (path === 'boards') {
            return pathname === `/w/${workspace.shortId}/boards`;
        }
        if (path === 'pages') {
            // Match pages list OR a specific page in this workspace
            return pathname.startsWith(`/w/${workspace.shortId}/pages`);
        }
        if (path === 'members') {
            return pathname === `/w/${workspace.shortId}/members`;
        }
        if (path === 'settings') {
            return pathname === `/w/${workspace.shortId}/settings`;
        }
        if (path === 'media') {
            return pathname === `/w/${workspace.shortId}/media`;
        }
        if (path === 'billing') {
            return pathname === `/w/${workspace.shortId}/billing`;
        }
        return false;
    };

    useEffect(() => {
        const element = elementRef.current;
        if (!element) return;

        const combined = combine(
            // Draggable configuration
            draggable({
                element,
                dragHandle: element,
                getInitialData: () => ({ workspace, index, type: 'WORKSPACE' }),
                onDragStart() {
                    isDraggingRef.current = true;
                    setIsDragging(true); // Set immediately on drag start
                },
                onDrag() {
                    setIsDragging(true);
                },
                onDrop() {
                    // Reset states immediately on drop
                    isDraggingRef.current = false;
                    setIsDragging(false);
                },
                onDragEnd() {
                    // Reset states immediately on drag end
                    isDraggingRef.current = false;
                    setIsDragging(false);
                },
            }),

            // Drop target configuration
            dropTargetForElements({
                element,
                getData: ({ input, element }) => {
                    return attachClosestEdge({ workspace, index, type: 'WORKSPACE' }, {
                        input,
                        element,
                        allowedEdges: ['top', 'bottom'],
                        threshold: 0.5,
                    });
                },
                canDrop: ({ source }) => source.data.type === 'WORKSPACE',
                onDrag(args) {
                    const { source, self } = args;

                    if (!self || !source) return;

                    // Skip if dragging the same workspace
                    if (self.data.workspace._id === source.data.workspace._id) {
                        setIsDragging(true);
                        return;
                    }

                    // Extract edge information for visual feedback
                    const edge = extractClosestEdge(self.data);
                    setDropEdge(edge);
                    setIsDropTarget(true);
                },
                onDragLeave: () => {
                    setIsDropTarget(false);
                    setDropEdge(null);
                },
                onDrop: ({ source, self }) => {
                    const edge = extractClosestEdge(self.data);
                    setIsDropTarget(false);
                    setIsDragging(false);
                    setDropEdge(null);
                    onReorder(source.data.index, index, edge);

                },
            })
        );

        return combined;
    }, [workspace, index, onReorder]);



    const handleClick = () => {
        // Only allow toggle if not currently dragging and workspaces are not collapsed
        if (!isDraggingRef.current) {
            toggleOptions(workspace.name);
        }
    };

    const handleCreatePage = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        try {
            const page = await dispatch(createPage({
                workspaceId: workspace.shortId,
                pageData: {
                    title: 'New page',
                    content: {
                        root: {
                            children: [],
                            direction: null,
                            format: '',
                            indent: 0,
                            type: 'root',
                            version: 1,
                        },
                    },
                },
            })).unwrap();
            if (page?.shortId) {
                navigate(`/w/${workspace.shortId}/pages/${page.shortId}`);
            }
        } catch (error) {
            console.error('Failed to create page:', error);
        }
    };

    const style = {
        transition: 'opacity 0.2s ease',
        position: 'relative',
    };

    // Collapse all workspaces when any workspace is being dragged
    const isWorkspaceOpen = openWorkspaces[workspace.name];

    return (
        <div
            className="zoobbe-workspace"
            style={style}
        >
            {/* Drop indicator - absolute positioned to prevent layout shift */}
            {isDropTarget && dropEdge && (
                <div
                    style={{
                        position: 'absolute',
                        left: '5px',
                        right: 0,
                        height: '2px',
                        width: 'calc(100% - 10px)',
                        backgroundColor: 'var(--brand-color)',
                        zIndex: 1000,
                        // // Fix: Use consistent positioning for adjacent drop zones
                        top: dropEdge === 'top' ? '-6px' : 'auto',
                        bottom: dropEdge === 'bottom' ? '-6px' : 'auto',
                        borderRadius: '50px',
                    }}
                />
            )}


            <div
                className="zoobbe-workspace-header"
                onClick={handleClick}
                ref={elementRef}
                style={{
                    cursor: isDragging ? 'grabbing' : '',
                    ...(isDragging ? { opacity: 0.3 } : {}),
                }}
            >
                <div className="workspace-name">
                    {
                        workspace?.subscription && workspace?.subscription?.planType !== 'Free' ? (
                            <span
                                className="zoobbe-workspace-icon"
                                data-tooltip-content={workspace?.subscription?.planType}
                                data-tooltip-position="top"
                            >
                                {
                                    workspace?.subscription?.planType === 'Premium' ? (
                                        // Placeholder SVG for "Premium"
                                        <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24" fill="#e3e3e3"><path d="m387-412 35-114-92-74h114l36-112 36 112h114l-93 74 35 114-92-71zM240-40v-309q-38-42-59-96t-21-115q0-134 93-227t227-93 227 93 93 227q0 61-21 115t-59 96v309l-240-80zm240-280q100 0 170-70t70-170-70-170-170-70-170 70-70 170 70 170 170 70M320-159l160-41 160 41v-124q-35 20-75.5 31.5T480-240t-84.5-11.5T320-283zm160-62" /></svg>
                                    ) : (
                                        // Placeholder SVG for "Other Paid Plans"
                                        <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24" fill="#e3e3e3"><path d="M480-120 80-600l120-240h560l120 240zm-95-520h190l-60-120h-70zm55 347v-267H218zm80 0 222-267H520zm144-347h106l-60-120H604zm-474 0h106l60-120H250z" /></svg>
                                    )
                                }
                            </span>
                        ) : (
                            <span
                                className="zoobbe-workspace-icon"
                                data-tooltip-content="Free"
                                data-tooltip-position="top"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24" fill="#e3e3e3"><path d="M240-120q-66 0-113-47T80-280t47-113 113-47 113 47 47 113-47 113-113 47m480 0q-66 0-113-47t-47-113 47-113 113-47 113 47 47 113-47 113-113 47m-480-80q33 0 56.5-23.5T320-280t-23.5-56.5T240-360t-56.5 23.5T160-280t23.5 56.5T240-200m480 0q33 0 56.5-23.5T800-280t-23.5-56.5T720-360t-56.5 23.5T640-280t23.5 56.5T720-200M480-520q-66 0-113-47t-47-113 47-113 113-47 113 47 47 113-47 113-113 47m0-80q33 0 56.5-23.5T560-680t-23.5-56.5T480-760t-56.5 23.5T400-680t23.5 56.5T480-600M240-280" /></svg>
                            </span>
                        )
                    }
                    <span className="zoobbe-workspace-title">
                        {workspace.name}
                        {workspace?.whiteLabelBinding?.instanceId && isAdmin && (
                            <span
                                className="workspace-bound-badge"
                                data-tooltip-content="This workspace is bound to a white-label domain"
                                data-tooltip-position="top"
                                style={{
                                    marginLeft: '6px',
                                    fontSize: '10px',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    color: 'var(--color-white)',
                                    fontWeight: '600',
                                    border: '1px solid var(--popover-border-color)',
                                }}
                            >
                                W/L
                            </span>
                        )}
                    </span>
                </div>

                <svg width="20" height="20" role="presentation" focusable="false" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.2929 16.7071L4.22185 9.63606C3.83132 9.24554 3.83132 8.61237 4.22185 8.22185C4.61237 7.83133 5.24554 7.83133 5.63606 8.22185L12 14.5858L18.364 8.22185C18.7545 7.83132 19.3877 7.83132 19.7782 8.22185C20.1687 8.61237 20.1687 9.24554 19.7782 9.63606L12.7071 16.7071C12.3166 17.0977 11.6834 17.0977 11.2929 16.7071Z" fill={`var(--primary-text-color)`}></path>
                </svg>
            </div>
            {isWorkspaceOpen && (
                <>
                    <ul className="zoobbe-workspace-options">
                        <li className={isActive('boards') ? 'active' : ''}><Link to={`/w/${workspace.shortId}/boards`}>
                            <span className="material-symbols-outlined">
                                workspaces
                            </span>
                            Boards
                        </Link></li>
                        <li className={`pages-option ${isActive('pages') ? 'active' : ''}`}>
                            <Link to={`/w/${workspace.shortId}/pages`}>
                                <span className="material-symbols-outlined">
                                    description
                                </span>
                                Pages

                                <div className="option-actions">
                                    <button type="button" className="icon-btn" onClick={handleCreatePage} data-tooltip-position="top" data-tooltip-content="Create page" aria-label="Create page">
                                        <Add size={18} />
                                    </button>
                                    <Link to={`/w/${workspace.shortId}/pages`} className="icon-btn" title="Open Pages" aria-label="Open Pages">
                                        <KeyboardArrowRight size={20} />
                                    </Link>
                                </div>
                            </Link>

                        </li>
                        <li className={isActive('members') ? 'active' : ''}><Link to={`/w/${workspace.shortId}/members`}>
                            <span className="material-symbols-outlined">
                                groups_2
                            </span>
                            Members
                        </Link></li>
                        <li className={isActive('media') ? 'active' : ''}><Link to={`/w/${workspace.shortId}/media`}>
                            <span className="material-symbols-outlined">
                                folder_open
                            </span>
                            Media
                        </Link></li>
                        <li className={isActive('settings') ? 'active' : ''}><Link to={`/w/${workspace.shortId}/settings`}>
                            <span className="material-symbols-outlined">
                                settings
                            </span>
                            Settings
                        </Link></li>
                        {/* Only show billing link for workspace admins */}
                        {isAdmin && (
                            <li className={isActive('billing') ? 'active' : ''}>
                                {/* {
                                    workspace?.subscription && workspace?.subscription?.planType !== 'Free' && (
                                        <Link to={`/w/${workspace.shortId}/billing`}>
                                            <span className="material-symbols-outlined">
                                                payments
                                            </span>
                                            Billing
                                        </Link>
                                    )
                                } */}

                                <Link to={`/w/${workspace.shortId}/billing`}>
                                    <span className="material-symbols-outlined">
                                        payments
                                    </span>
                                    Billing
                                </Link>
                            </li>
                        )}
                    </ul>

                    {/* Show trial/upgrade card for admins when no active paid plan */}
                    {showUpgradeCard && (
                        <ZoobbePremiumCard workspaceId={workspace.shortId} planType="Premium" ctaMode={ctaMode} />
                        // <ZoobbeBlackFridayCard workspaceId={workspace.shortId} />

                    )}
                </>
            )}
        </div>
    );
};

// Simplified workspace item for guest workspaces (no drag-and-drop)
const WorkspaceSidebar = ({
    openWorkspaces: propsOpenWorkspaces,
    setOpenWorkspaces: propsSetOpenWorkspaces,
    isEmbedded = true
}) => {
    const { workspaces, status } = useSelector(state => state.workspaces);
    // Get current user to trigger re-fetch on account switch
    const user = useSelector((state) => state.user.user);

    // Internal state if props are not provided
    const [internalOpenWorkspaces, setInternalOpenWorkspaces] = useState({});

    // Use props if provided, otherwise use internal state
    const openWorkspaces = propsOpenWorkspaces || internalOpenWorkspaces;
    const setOpenWorkspaces = propsSetOpenWorkspaces || setInternalOpenWorkspaces;

    // Initialize openWorkspaces if using internal state (merge new entries, preserve existing toggle state)
    useEffect(() => {
        if (!propsOpenWorkspaces && workspaces.length > 0) {
            setInternalOpenWorkspaces(prev => {
                const updated = { ...prev };
                workspaces.forEach((ws, index) => {
                    if (!(ws.name in updated)) {
                        updated[ws.name] = index < 3;
                    }
                });
                return updated;
            });
        }
    }, [workspaces, propsOpenWorkspaces]);

    const [width, setWidth] = useState(() => {
        const savedWidth = localStorage.getItem('expandableColumnWidth');
        return savedWidth ? parseInt(savedWidth, 10) : 290;
    });

    const [isResizing, setIsResizing] = useState(false);
    const resizeRef = useRef(null);

    // Listen for storage changes to sync width across components
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'expandableColumnWidth' && e.newValue) {
                setWidth(parseInt(e.newValue, 10));
            }
        };

        const handleCustomWidthChange = (e) => {
            setWidth(e.detail.width);
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('expandableColumnWidthChange', handleCustomWidthChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('expandableColumnWidthChange', handleCustomWidthChange);
        };
    }, []);

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(fetchWorkspaces());
        dispatch(fetchGuestsWorkspaces());
    }, [dispatch, user]);

    // Monitor for drag and drop events
    useEffect(() => {
        return monitorForElements({
            onDragStart({ source }) {
                if (source.data.type === 'WORKSPACE') {
                    setOpenWorkspaces(prevState => {
                        const newState = {};
                        Object.keys(prevState).forEach(key => {
                            newState[key] = false; // close all workspaces
                        });
                        return newState;
                    });

                }
            },
            onDrop({ source, location }) {
                // const destination = location.current.dropTargets[0];
                // if (!destination || source.data.type !== 'WORKSPACE') {
                //     setOpenWorkspaces(prevState => {
                //         const newState = {};
                //         Object.keys(prevState).forEach(key => {
                //             newState[key] = false; // close all workspaces
                //         });
                //         return newState;
                //     });
                //     return;
                // }

                // // Handle workspace reordering logic here
                // console.log('Workspace dropped:', source.data, destination.data);
            },
            onDragEnd({ source }) {
                // if (source.data.type === 'WORKSPACE') {
                //     // Ensure we reset the state after a short delay to allow for animations
                //     setTimeout(() => {
                //         setOpenWorkspaces(prevState => {
                //             const newState = {};
                //             Object.keys(prevState).forEach(key => {
                //                 newState[key] = false; // close all workspaces
                //             });

                //             return newState;
                //         });
                //     }, 100);
                // }
            },
            onDragCancel({ source }) {
                // if (source.data.type === 'WORKSPACE') {
                //     setOpenWorkspaces(prevState => {
                //         const newState = {};
                //         Object.keys(prevState).forEach(key => {
                //             newState[key] = false; // close all workspaces
                //         });

                //         return newState;
                //     });
                // }
            }
        });
    }, []);

    const handleWorkspaceReorder = (sourceIndex, targetIndex, edge) => {
        const newWorkspaces = [...workspaces];
        const [movedWorkspace] = newWorkspaces.splice(sourceIndex, 1);

        // Calculate correct insert position based on edge
        let insertIndex;
        if (sourceIndex < targetIndex) {
            // When moving downwards
            insertIndex = edge === 'bottom' ? targetIndex : targetIndex - 1;
        } else {
            // When moving upwards
            insertIndex = edge === 'bottom' ? targetIndex + 1 : targetIndex;
        }

        // Ensure insertIndex is within bounds
        insertIndex = Math.max(0, Math.min(insertIndex, newWorkspaces.length));

        newWorkspaces.splice(insertIndex, 0, movedWorkspace);

        // Optimistic update while preserving workspace states
        dispatch(reorderWorkspaces(newWorkspaces));

        // Send to backend
        const workspaceOrder = newWorkspaces.map(ws => ws._id);
        dispatch(updateWorkspaceOrder(workspaceOrder));
    };

    const toggleOptions = (workspaceName) => {
        setOpenWorkspaces(prevState => {
            return ({
                ...prevState,
                [workspaceName]: !prevState[workspaceName]
            });
        });
    };

    // Handle mouse down on resize handle
    const handleMouseDown = (e) => {
        setIsResizing(true);
        e.preventDefault();
    };

    // Handle mouse up for resizing
    const handleMouseUp = useCallback(() => {
        setIsResizing(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        // Save the width to localStorage
        localStorage.setItem('expandableColumnWidth', width.toString());

        // Dispatch custom event for same-tab synchronization
        window.dispatchEvent(new CustomEvent('expandableColumnWidthChange', {
            detail: { width }
        }));
    }, [width, isResizing]);

    // Handle mouse move for resizing
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizing) return;

            const newWidth = e.clientX;
            const minWidth = 250;
            const maxWidth = Math.min(600, window.innerWidth * 0.4); // Max 40% of screen width or 600px

            if (newWidth >= minWidth && newWidth <= maxWidth) {
                setWidth(newWidth);
            }
        };

        if (isResizing) {
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isResizing, handleMouseUp]);

    const ContainerTag = isEmbedded ? 'div' : 'aside';

    return (
        <ContainerTag
            className={`zoobbe-sidebar ${isEmbedded ? 'embedded' : ''}${isResizing ? ' resizing' : ''}`}
            style={isEmbedded ? {} : { width: `${width - 40}px`, minWidth: `${width - 40}px` }}
        >
            <nav className="zoobbe-sidebar-nav">
                {
                    workspaces?.length > 0 && (
                        <div className="zoobbe-workspaces">
                            {!isEmbedded && (
                                <h3>
                                    Workspaces
                                </h3>
                            )}
                            {
                                status === 'loading' && (workspaces?.length < 1) ? (
                                    <div className='spinner-container'>
                                        <BouncingDots />
                                    </div>
                                ) : (
                                    <>
                                        {workspaces.map((workspace, index) => (
                                            <DraggableWorkspaceItem
                                                key={workspace._id}
                                                workspace={workspace}
                                                index={index}
                                                onReorder={handleWorkspaceReorder}
                                                openWorkspaces={openWorkspaces}
                                                toggleOptions={toggleOptions}
                                                setOpenWorkspaces={setOpenWorkspaces}
                                            />
                                        ))}
                                    </>
                                )
                            }
                        </div>
                    )
                }
            </nav>

            {!isEmbedded && (
                <div
                    className={`expandable-column__resize-handle ${isResizing ? 'resizing' : ''}`}
                    onMouseDown={handleMouseDown}
                    ref={resizeRef}
                />
            )}
        </ContainerTag>
    );
};

export default WorkspaceSidebar;